import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";

const StatusModal = ({
  isOpen,
  toggle,
  status: currentStatus,
  updateOrderStatus,
  orderId,
  order,
  isServiceDeleted = false
}) => {
  const [newStatus, setNewStatus] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Status configuration with translations and rules
  const statusConfig = {
    pending: {
      label: "قيد الانتظار",
      color: "warning",
      canChangeTo: ["approved", "progress", "cancelled"],
      requiresConfirmation: false
    },
    approved: {
      label: "تم الموافقة", 
      color: "info",
      canChangeTo: ["progress", "completed", "cancelled", "cancelled_by_provider"],
      requiresConfirmation: false
    },
    progress: {
      label: "قيد التنفيذ",
      color: "primary",
      canChangeTo: ["completed", "cancelled_by_provider"],
      requiresConfirmation: false
    },
    completed: {
      label: "مكتمل",
      color: "success",
      canChangeTo: ["refunded"],
      requiresConfirmation: true
    },
    cancelled: {
      label: "الغاء الطلب",
      color: "secondary",
      canChangeTo: [],
      requiresConfirmation: true
    },
    cancelled_by_user: {
      label: "ملغى من المستخدم",
      color: "secondary",
      canChangeTo: ["refunded"],
      requiresConfirmation: true
    },
    cancelled_by_provider: {
      label: "إلغاء الطلب بعد الدفع ",
      color: "danger",
      canChangeTo: ["refunded"],
      requiresConfirmation: true,
      requiresPaid: true
    },
    cancelled_by_admin: {
      label: "ملغى من الإدارة",
      color: "dark",
      canChangeTo: ["refunded"],
      requiresConfirmation: true
    },

  };

  // Safe access to status config with fallback
  const getStatusConfig = (status) => {
    return statusConfig[status] || {
      label: status,
      canChangeTo: [],
      requiresConfirmation: false
    };
  };

  const handleStatusChange = (event) => {
    const selectedStatus = DOMPurify.sanitize(event.target.value);
    const currentConfig = getStatusConfig(currentStatus);
    
    // Validate the status change - prevent changes from terminal statuses
    if (["completed", "refunded", "cancelled", "cancelled_by_user", "cancelled_by_provider", "cancelled_by_admin"].includes(currentStatus)) {
      // Only allow transition to refunded from completed or cancelled statuses
      if (selectedStatus !== "refunded") {
        toast.error("لا يمكن تغيير حالة الطلب بعد اكتماله أو ملغيته");
        return;
      }
    }

    // Block moving to progress if payment is not paid
    if (selectedStatus === "progress" && order.paymentStatus !== "paid") {
      toast.error("لا يمكن الانتقال إلى قيد التنفيذ قبل إتمام الدفع");
      return;
    }

    // Block moving to completed if payment is not paid
    if (selectedStatus === "completed" && order.paymentStatus !== "paid") {
      toast.error("لا يمكن تعيين الطلب كمكتمل قبل إتمام الدفع");
      return;
    }

    // Block cancelled_by_provider if payment is not paid
    if (selectedStatus === "cancelled_by_provider" && order.paymentStatus !== "paid") {
      toast.error("لا يمكن إلغاء الطلب من المزود قبل إتمام الدفع. استخدم 'ملغى' بدلاً من ذلك");
      return;
    }

    // Block cancelled if payment is already paid
    if (selectedStatus === "cancelled" && order.paymentStatus === "paid") {
      toast.error("لا يمكن استخدام 'ملغى' بعد إتمام الدفع. استخدم 'ملغى من المزود' بدلاً من ذلك");
      return;
    }

    if (!currentConfig.canChangeTo.includes(selectedStatus)) {
      toast.error("تغير الحالة غير مسموح به");
      return;
    }

    setNewStatus(selectedStatus);
    
    // Show confirmation for terminal or significant statuses
    if (["completed", "refunded", "cancelled", "cancelled_by_user", "cancelled_by_provider", "cancelled_by_admin"].includes(selectedStatus)) {
      setShowConfirm(true);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      toast.error("يجب اختيار حالة جديدة");
      return;
    }

    // Defensive check in case status is set programmatically
    if (newStatus === "completed" && order?.paymentStatus !== "paid") {
      toast.error("لا يمكن تعيين الطلب كمكتمل قبل إتمام الدفع");
      return;
    }

    setLoading(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(`تم تحديث الحالة إلى ${getStatusConfig(newStatus).label} بنجاح`);
      toggle();
      setNewStatus("");
    } catch (error) {
      toast.error("فشل في تحديث الحالة");
      console.error("Update error:", error);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  // Get available status options based on current status
  const getAvailableStatusOptions = () => {
    const currentConfig = getStatusConfig(currentStatus);
    const isPaymentPaid = order?.paymentStatus === "paid";

    return Object.keys(statusConfig)
      .filter(status => currentConfig.canChangeTo.includes(status))
      .filter(status => status !== "completed" || isPaymentPaid)
      .map(status => ({
        value: status,
        label: getStatusConfig(status).label
      }));
  };

  return (
    <>
      {/* Main Status Update Modal */}
      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>تحديث حالة الطلب</ModalHeader>
        <ModalBody>
          {isServiceDeleted && (
            <div className="alert alert-warning d-flex align-items-start mb-3">
              <i className="bx bx-error-circle me-2 mt-1" style={{ fontSize: '20px' }}></i>
              <div>
                <strong>⚠️ الخدمة مؤرشفة</strong>
                <p className="mb-0 mt-1 small">
                  تم أرشفة الخدمة المرتبطة بهذا الطلب. لا يمكن تغيير حالة الطلب إلا بعد استعادة الخدمة.
                </p>
              </div>
            </div>
          )}

          <div className="mb-3">
            <span className={`badge bg-${getStatusConfig(currentStatus).color} text-white`}>
              الحالة الحالية: <strong>{getStatusConfig(currentStatus).label}</strong>
            </span>
          </div>

          {!isServiceDeleted && (
            <div className="form-group">
              <label>اختر الحالة الجديدة:</label>
              <select
                className="form-control"
                value={newStatus}
                onChange={handleStatusChange}
                disabled={loading}
              >
                <option value="" disabled>اختر حالة جديدة</option>
                {getAvailableStatusOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="primary" 
            onClick={handleStatusUpdate}
            disabled={!newStatus || loading || isServiceDeleted}
          >
            {loading ? <Spinner size="sm" /> : "تحديث الحالة"}
          </Button>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            إلغاء
          </Button>
        </ModalFooter>
      </Modal>

      {/* Completion Confirmation Modal */}
      <Modal isOpen={showConfirm} centered>
        <ModalHeader toggle={() => setShowConfirm(false)}>
          {newStatus === "cancelled" && "تأكيد الإلغاء"}
          {newStatus === "cancelled_by_provider" && "تأكيد الإلغاء من المزود"}
          {newStatus === "cancelled_by_admin" && "تأكيد الإلغاء من الإدارة"}
          {newStatus === "cancelled_by_user" && "تأكيد الإلغاء من المستخدم"}
          {newStatus === "completed" && "تأكيد الإكتمال"}
          {newStatus === "refunded" && "تأكيد استرجاع المبلغ"}
        </ModalHeader>
        <ModalBody>
          {newStatus === "cancelled" && (
            <div className="alert alert-secondary">
              <strong>تنبيه:</strong> سيتم إلغاء هذا الطلب. هذا الخيار متاح قبل إتمام الدفع فقط.
            </div>
          )}
          {newStatus === "cancelled_by_provider" && (
            <div className="alert alert-danger">
              <strong>تنبيه:</strong> سيؤدي إلغاء هذا الطلب من قبل المزود إلى زيادة عداد الإلغاءات لملفك. تأكد من أنك متأكد من هذا القرار.
            </div>
          )}
          {newStatus === "cancelled_by_admin" && (
            <div className="alert alert-dark">
              <strong>تنبيه:</strong> سيتم إلغاء هذا الطلب بواسطة الإدارة. قد يتم تطبيق حقوق عقابية.
            </div>
          )}
          {newStatus === "cancelled_by_user" && (
            <div className="alert alert-secondary">
              <strong>تنبيه:</strong> سيتم إلغاء هذا الطلب من قبل المستخدم.
            </div>
          )}
          {newStatus === "completed" && (
            <div className="alert alert-warning">
              <strong>تنبيه:</strong> إذا قمت بتحديث الحالة إلى "مكتمل"، فلن تتمكن من إعادة التغيير.
            </div>
          )}
          {newStatus === "refunded" && (
            <div className="alert alert-info">
              <strong>تنبيه:</strong> سيتم استرجاع المبلغ للمستخدم.
            </div>
          )}
          <p>هل أنت متأكد أنك تريد المتابعة؟</p>
        </ModalBody>
        <ModalFooter>
          <Button 
            color={getStatusConfig(newStatus).color}
            onClick={handleStatusUpdate}
          >
            نعم، تأكيد
          </Button>
          <Button color="secondary" onClick={() => setShowConfirm(false)}>
            إلغاء
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

StatusModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
  updateOrderStatus: PropTypes.func.isRequired,
  orderId: PropTypes.string.isRequired,
  order: PropTypes.shape({
    paymentStatus: PropTypes.string
  }).isRequired,
  isServiceDeleted: PropTypes.bool
};

export default StatusModal;